/**
 * Session monitoring — tracks JWT expiration, user activity,
 * and handles automatic token refresh.
 *
 * Since JWT is HttpOnly, we fetch session metadata from the backend
 * via /api/auth/session-info rather than parsing cookies client-side.
 */
import { authMutations } from './authState'
import router from '@/router'

const BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8080'

class SessionMonitor {
	constructor() {
		this.checkInterval = 30000 // Check every 30 seconds
		this.idleTimeout = 900     // 15 minutes of inactivity

		// Thresholds are computed dynamically based on token lifetime in updateThresholds()
		this.warningThreshold = 60
		this.refreshThreshold = 2100
		this.tokenLifetime = null

		this.intervalId = null
		this.lastActivity = Date.now()
		this.tokenExpiry = null
		this.sessionStarted = null
		this.provider = null
		this.warningShown = false
		this.refreshInProgress = false
		this.activityListeners = null

		this.checkSession = this.checkSession.bind(this)
	}

	/**
	 * Start monitoring the session.
	 */
	async start() {
		// Prevent double-start
		if (this.intervalId) return

		await this.fetchSessionInfo()

		if (!this.tokenExpiry) {
			return
		}

		this.sessionStarted = Date.now()
		this.lastActivity = Date.now()

		this.startActivityTracking()
		this.intervalId = setInterval(this.checkSession, this.checkInterval)
		this.checkSession()
	}

	/**
	 * Stop monitoring.
	 */
	stop() {
		if (this.intervalId) {
			clearInterval(this.intervalId)
			this.intervalId = null
		}

		this.stopActivityTracking()
		this.tokenExpiry = null
		this.sessionStarted = null
		this.warningShown = false
		this.refreshInProgress = false
	}

	/**
	 * Fetch session metadata from backend.
	 * Returns { expiresAt, issuedAt, provider, timeRemaining }.
	 */
	async fetchSessionInfo() {
		try {
			const response = await fetch(`${BASE_URL}/api/auth/session-info`, {
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' }
			})

			if (!response.ok) return null

			const info = await response.json()
			if (info.error) return null

			this.tokenExpiry = info.expiresAt
			this.sessionStarted = info.issuedAt || Date.now()
			this.provider = info.provider

			// Compute proportional thresholds based on actual token lifetime
			this.updateThresholds()

			return info
		} catch (e) {
			return null
		}
	}

	/**
	 * Compute refresh/warning thresholds proportional to the token's actual lifetime.
	 * This ensures correct behavior whether the IdP issues 5-minute or 60-minute tokens.
	 *
	 * - Refresh at 50% of lifetime remaining
	 * - Warn at 15% of lifetime remaining (clamped to 30–60s)
	 * - Idle timeout capped to 80% of lifetime
	 * - Check interval scales down for short-lived tokens
	 */
	updateThresholds() {
		if (!this.tokenExpiry || !this.sessionStarted) return

		this.tokenLifetime = Math.round((this.tokenExpiry - this.sessionStarted) / 1000)
		this.refreshThreshold = Math.round(this.tokenLifetime * 0.5)
		this.warningThreshold = Math.min(60, Math.max(30, Math.round(this.tokenLifetime * 0.15)))
		this.idleTimeout = Math.min(this.idleTimeout, Math.round(this.tokenLifetime * 0.8))

		if (this.tokenLifetime < 300) {
			this.checkInterval = 10000
			if (this.intervalId) {
				clearInterval(this.intervalId)
				this.intervalId = setInterval(this.checkSession, this.checkInterval)
			}
		}
	}

	/**
	 * Re-fetch session info after a refresh.
	 */
	async updateSessionInfo() {
		const info = await this.fetchSessionInfo()
		if (info) {
			this.warningShown = false
		}
		return info
	}

	/**
	 * Track user activity (debounced).
	 */
	startActivityTracking() {
		const trackActivity = () => {
			this.lastActivity = Date.now()
		}

		const events = ['mousemove', 'keydown', 'click', 'scroll']
		events.forEach(evt => document.addEventListener(evt, trackActivity, { passive: true }))

		this.activityListeners = { trackActivity, events }
	}

	stopActivityTracking() {
		if (this.activityListeners) {
			const { trackActivity, events } = this.activityListeners
			events.forEach(evt => document.removeEventListener(evt, trackActivity))
			this.activityListeners = null
		}
	}

	/**
	 * Periodic session check — refresh, warn, or expire.
	 */
	async checkSession() {
		if (!this.tokenExpiry) return

		const now = Date.now()
		const timeRemaining = this.tokenExpiry - now
		const idleTime = now - this.lastActivity

		// Expired
		if (timeRemaining <= 0) {
			this.handleExpiredSession()
			return
		}

		// Idle too long
		if (idleTime > this.idleTimeout * 1000) {
			if (!this.warningShown) {
				this.showIdleWarning(timeRemaining)
			}
			return
		}

		// Expiration warning takes priority — show it even if we could still refresh
		if (timeRemaining <= this.warningThreshold * 1000) {
			if (!this.warningShown) {
				this.showExpirationWarning(timeRemaining)
			}
		} else if (timeRemaining <= this.refreshThreshold * 1000 && !this.refreshInProgress) {
			// Silently refresh while user is active, between warning and refresh thresholds
			await this.refreshSession()
		}
	}

	/**
	 * Call backend to refresh the JWT cookie.
	 */
	async refreshSession() {
		if (this.refreshInProgress) return

		this.refreshInProgress = true

		try {
			const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' }
			})

			if (response.ok) {
				await this.updateSessionInfo()
				this.emitEvent('session-refreshed', { newExpiry: this.tokenExpiry })
			} else if (response.status === 401) {
				this.handleExpiredSession()
			}
		} catch (e) {
			// Refresh failed — will retry next check cycle
		} finally {
			this.refreshInProgress = false
		}
	}

	showExpirationWarning(timeRemaining) {
		this.warningShown = true
		const minutes = Math.round(timeRemaining / 60000)

		this.emitEvent('session-warning', {
			type: 'expiration',
			message: `Your session will expire in ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
			timeRemaining,
			action: () => this.refreshSession()
		})
	}

	showIdleWarning(timeRemaining) {
		this.warningShown = true

		this.emitEvent('session-warning', {
			type: 'idle',
			message: 'You have been inactive for a while. Your session may expire.',
			timeRemaining,
			action: () => {
				this.lastActivity = Date.now()
				this.warningShown = false
				this.refreshSession()
			}
		})
	}

	handleExpiredSession() {
		this.stop()
		authMutations.reset()
		this.emitEvent('session-expired', {
			message: 'Your session has expired. Please log in again.'
		})
		router.push('/login?expired=true')
	}

	emitEvent(name, detail) {
		window.dispatchEvent(new CustomEvent(name, { detail }))
	}

	/**
	 * Get current session state (for debug or display).
	 */
	getSessionInfo() {
		if (!this.tokenExpiry) return null

		const now = Date.now()
		const timeRemaining = this.tokenExpiry - now
		return {
			provider: this.provider || 'unknown',
			expiresAt: this.tokenExpiry,
			timeRemaining,
			sessionDuration: now - this.sessionStarted,
			idleTime: now - this.lastActivity,
			isRefreshing: this.refreshInProgress,
			warningShown: this.warningShown,
			percentRemaining: Math.max(0, Math.min(100, (timeRemaining / ((this.tokenLifetime || 3600) * 1000)) * 100))
		}
	}
}

// Named export to match existing import pattern
export const sessionMonitor = new SessionMonitor()
